import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Stack,
  TextField,
  InputAdornment,
  useMediaQuery
} from "@mui/material";
import {
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  TrendingUp,
  TrendingDown,
  LibraryBooks as LibraryBooksIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import axios from "axios";

// Национални средни стойности
const NATIONAL_AVERAGES = {
  stateSubsidyPerCapita: 23.37,
  revenueFromSubsidiesPercent: 69.3,
  revenueFromRentPercent: 5.5,
  revenueFromOtherPercent: 25.2,
  expensesForSalariesPercent: 59.4,
  expensesOtherPercent: 40.6,
  staffHigherEducationPercent: 48,
  secretariesHigherEducationPercent: 24.1,
  averageInsuranceIncome: 1028,
  chitalishtaNoTrainingPercent: 70,
  chitalishtaPer10kResidents: 5.5,
  chitalishtaPer1kChildrenUnder15: 3.9,
  chitalishtaPer1kElderly: 2.4,
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metrics-tabpanel-${index}`}
      aria-labelledby={`metrics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const ComparisonIndicator = ({ value, average, isHigherBetter = true }) => {
  if (!value || !average) return null;

  const diff = value - average;
  const percentDiff = average > 0 ? ((diff / average) * 100).toFixed(1) : 0;

  const isAboveAverage = diff >= 0;

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      {isAboveAverage ? (
        <TrendingUp
          sx={{ color: "success.main", fontSize: { xs: 14, sm: 15, md: 16 } }}
        />
      ) : (
        <TrendingDown
          sx={{ color: "error.main", fontSize: { xs: 14, sm: 15, md: 16 } }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color: isAboveAverage ? "success.main" : "error.main",
          fontWeight: 600,
          fontSize: { xs: "0.7rem", sm: "0.72rem", md: "0.75rem" },
        }}
      >
        {diff > 0 ? "+" : diff === 0 ? "" : ""}
        {diff.toFixed(2)} ({diff > 0 ? "+" : ""}
        {percentDiff}%)
      </Typography>
    </Box>
  );
};

const MetricRow = ({
  label,
  value,
  suffix = "",
  nationalAverage,
  isHigherBetter,
  showComparison = true,
}) => {
  const theme = useTheme();

  return (
    <TableRow sx={{ "&:hover": { bgcolor: "action.hover" } }}>
      <TableCell
        sx={{
          py: { xs: 0.75, sm: 1, md: 1.25 },
          width: "45%",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: {
              xs: "0.75rem", // Mobile (<600px)
              sm: "0.8rem", // Tablet (600-900px)
              md: "0.85rem", // 13" лаптоп (900-1200px)
              lg: "0.875rem", // Desktop (1200-1536px)
            },
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{
          py: { xs: 0.75, sm: 1, md: 1.25 },
          width: "25%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            fontSize: {
              xs: "0.85rem", // Mobile
              sm: "0.9rem", // Tablet
              md: "0.95rem", // 13" лаптоп
              lg: "1.05rem", // Desktop
            },
            whiteSpace: "nowra p",
          }}
        >
          {value !== null && value !== undefined ? `${value} ${suffix}` : "НЯМА ДАННИ"}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          py: { xs: 0.75, sm: 1, md: 1.25 },
          width: "30%",
        }}
      >
        {showComparison && nationalAverage !== undefined ? (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: {
                  xs: "0.68rem", // Mobile
                  sm: "0.7rem", // Tablet
                  md: "0.72rem", // 13" лаптоп
                  lg: "0.75rem", // Desktop
                },
                lineHeight: 1.2,
              }}
            >
              Средно: {nationalAverage} {suffix}
            </Typography>
            {value !== null && value !== undefined && (
              <ComparisonIndicator
                value={parseFloat(value)}
                average={nationalAverage}
                isHigherBetter={isHigherBetter}
              />
            )}
          </Box>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "0.68rem",
                sm: "0.7rem",
                md: "0.72rem",
              },
            }}
          >
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const MunicipalityMetricsTabs = ({ municipalityCode }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [chitalishta, setChitalishta] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [chitalishtaLoading, setChitalishtaLoading] = useState(false);
  const [error, setError] = useState(null);

  // Медия заявки за различни устройства
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 900px
  const isSmallLaptop = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900px - 1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // > 1200px

  // Определяне на режим на табовете
  const getTabMode = () => {
    if (isMobile) return "icons-only"; // Мобилни: само иконки
    if (isTablet) return "icons-text-stacked"; // Таблети: иконки отгоре + текст отдолу
    if (isSmallLaptop) return "icons-text-stacked"; // 13" лаптопи: иконки отгоре + текст отдолу
    return "icons-text-side"; // Десктоп: иконки отляво + текст
  };

  const tabMode = getTabMode();

  useEffect(() => {
    const fetchData = async () => {
      if (!municipalityCode) return;

      try {
        setLoading(true);
        setError(null);

        const metricsResponse = await axios.get(
          `https://danni.narodnichitalishta.bg/api/municipalities/${municipalityCode}/metrics`
        );
        setMetrics(metricsResponse.data);

        setChitalishtaLoading(true);
        const chitalishtaResponse = await axios.get(
          `https://danni.narodnichitalishta.bg/api/municipalities/${municipalityCode}/chitalishta`
        );
        setChitalishta(chitalishtaResponse.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Грешка при зареждане на данните");
      } finally {
        setLoading(false);
        setChitalishtaLoading(false);
      }
    };

    fetchData();
  }, [municipalityCode]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredChitalishta = chitalishta.filter(
    (ch) =>
      ch.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ch.town?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stringToColor = (string) => {
    if (!string) return theme.palette.primary.main;
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      "#4CAF50",
      "#2196F3",
      "#FF9800",
      "#9C27B0",
      "#F44336",
      "#00BCD4",
      "#FF5722",
      "#673AB7",
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Зареждане на показателите...
        </Typography>
      </Paper>
    );
  }

  if (error || !metrics) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || "Показателите не са налични"}
      </Alert>
    );
  }

  // Табове с информация
  const tabs = [
    {
      label: "Основна информация",
      icon: <InfoIcon />,
      stackedLabel: "Основна",
    },
    {
      label: "Приходи и разходи",
      icon: <MoneyIcon />,
      stackedLabel: "Приходи и разходи",
    },
    {
      label: "Персонал и заплащане",
      icon: <PeopleIcon />,
      stackedLabel: "Персонал и заплащане",
    },
    {
      label: "Читалищата и населението",
      icon: <GroupsIcon />,
      stackedLabel: "Читалищата и населението",
    },
    {
      label: "Читалища в общината",
      icon: <LibraryBooksIcon />,
      stackedLabel: "Читалища в общината",
    },
  ];

  // Определяне на текста за таба според режима
  const getTabText = (tab) => {
    if (tabMode === "icons-only") {
      return "";
    }
    return tabMode === "icons-text-stacked" ? tab.stackedLabel : tab.label;
  };

  const handleCardClick = (slug) => {
    const externalUrl = `https://karta.narodnichitalishta.bg/community-center/${slug}`;
    window.open(externalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
      {/* Табове - централно разпределени */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            color: theme.palette.custom.primaryOrange,
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.custom.primaryOrange,
            },
            minHeight: {
              xs: 56, // Mobile (<600px)
              sm: 72, // Tablet (600-900px) - по-високи за stack
              md: 80, // 13" лаптоп (900-1200px) - по-високи за stack
              lg: 64, // Desktop (1200-1536px) - по-ниски за side-by-side
            },
            "& .MuiTabs-flexContainer": {
              justifyContent: "space-evenly",
            },
            "& .MuiTab-root": {
              minHeight: {
                xs: 56, // Mobile
                sm: 72, // Tablet
                md: 80, // 13" лаптоп
                lg: 64, // Desktop
              },
              textTransform: "none",
              fontSize: {
                xs: "0.7rem", // Mobile (само иконки)
                sm: "0.7rem", // Tablet (stacked)
                md: "0.75rem", // 13" лаптоп (stacked)
                lg: "0.85rem", // Desktop (side-by-side)
              },
              fontWeight: 500,
              px: {
                xs: 0.5, // Mobile
                sm: 1, // Tablet
                md: 1.5, // 13" лаптоп
                lg: 2, // Desktop
              },
              // Ховър ефект
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              // Активен таб
              "&.Mui-selected": {
                color: theme.palette.custom.primaryOrange,
                fontWeight: 600,
              },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={
                tabMode === "icons-text-stacked" || tabMode === "icons-only"
                  ? tab.icon
                  : undefined
              }
              iconPosition={
                tabMode === "icons-text-stacked" || tabMode === "icons-only"
                  ? "top"
                  : "start"
              }
              label={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection:
                      tabMode === "icons-text-stacked" ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: tabMode === "icons-text-stacked" ? 0.25 : { lg: 0.75 },
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {tabMode === "icons-text-side" && tab.icon}
                  <Box
                    component="span"
                    sx={{
                      textAlign: "center",
                      lineHeight: 1.1,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxWidth: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                    }}
                  >
                    {getTabText(tab)}
                  </Box>
                </Box>
              }
              id={`metrics-tab-${index}`}
              aria-controls={`metrics-tabpanel-${index}`}
              sx={{
                // Адаптивни стилове за иконки
                "& .MuiTab-iconWrapper": {
                  marginBottom: tabMode === "icons-text-stacked" ? 0.5 : 0,
                  fontSize: {
                    xs: "1.4rem", // Mobile
                    sm: "1.3rem", // Tablet (по-малки за stack)
                    md: "1.4rem", // 13" лаптоп
                    lg: "1.5rem", // Desktop
                  },
                },
                // Стилове за stacked режим
                ...(tabMode === "icons-text-stacked" && {
                  minWidth: "auto",
                  padding: "12px 8px",
                  "& .MuiTab-iconWrapper": {
                    marginBottom: 0.5,
                  },
                }),
                // Стилове за icons-only режим
                ...(tabMode === "icons-only" && {
                  minWidth: "auto",
                  padding: "12px 8px",
                  "& .MuiTab-iconWrapper": {
                    marginBottom: 0.25,
                  },
                }),
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Съдържание на табовете */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 } }}>
        {/* Таб 1: Основна информация */}
        <TabPanel value={tabValue} index={0}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.custom.primaryOrange,
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: {
                xs: "1rem", // Mobile
                sm: "1.1rem", // Tablet
                md: "1.15rem", // 13" лаптоп
                lg: "1.25rem", // Desktop
              },
              textAlign: "center",
            }}
          >
            Основна информация
          </Typography>

          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Показател
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Стойност
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Сравнение
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <MetricRow
                  label="Брой читалища"
                  value={metrics.totalChitalishta}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Брой селски читалища (2023)"
                  value={metrics.villageChitalishta}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Брой градски читалища (2023)"
                  value={metrics.cityChitalishta}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Размер на държавната субсидия за читалищата (2025)"
                  value={metrics.stateSubsidyAmount?.toFixed(0)}
                  suffix="лв."
                  showComparison={false}
                />
                <MetricRow
                  label="Държавна субсидия на човек от населението (2025)"
                  value={metrics.stateSubsidyPerCapita?.toFixed(2)}
                  suffix="лв."
                  nationalAverage={NATIONAL_AVERAGES.stateSubsidyPerCapita}
                  isHigherBetter={false}
                />
                <MetricRow
                  label="Допълнително отпуснати бройки за читалищата (2021-2023)"
                  value={metrics.additionalPositions?.toFixed(0)}
                  suffix="бр."
                  showComparison={false}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Таб 2: Приходи и разходи */}
        <TabPanel value={tabValue} index={1}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.custom.primaryOrange,
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: {
                xs: "1rem", // Mobile
                sm: "1.1rem", // Tablet
                md: "1.15rem", // 13" лаптоп
                lg: "1.25rem", // Desktop
              },
              textAlign: "center",
            }}
          >
            Приходи и разходи
          </Typography>

          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Показател
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Стойност
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Сравнение
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <MetricRow
                  label="Процент на приходите от субсидия от държавния/общинския бюджети (НСИ, 2022)"
                  value={metrics.revenueFromSubsidiesPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={
                    NATIONAL_AVERAGES.revenueFromSubsidiesPercent
                  }
                  isHigherBetter={false}
                />
                <MetricRow
                  label="Процент на приходите от наеми на движимо и недвижимо имущество (НСИ, 2022)"
                  value={metrics.revenueFromRentPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={NATIONAL_AVERAGES.revenueFromRentPercent}
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Процент на приходите от други източници (НСИ, 2022)"
                  value={metrics.revenueFromOtherPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={NATIONAL_AVERAGES.revenueFromOtherPercent}
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Процент на разходите за работна заплата, социални осигуровки и надбавки (НСИ, 2022)"
                  value={metrics.expensesForSalariesPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={NATIONAL_AVERAGES.expensesForSalariesPercent}
                  isHigherBetter={false}
                />
                <MetricRow
                  label="Процент на другите разходи (НСИ, 2022)"
                  value={metrics.expensesOtherPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={NATIONAL_AVERAGES.expensesOtherPercent}
                  isHigherBetter={true}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Таб 3: Персонал и заплащане */}
        <TabPanel value={tabValue} index={2}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.custom.primaryOrange,
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: {
                xs: "1rem", // Mobile
                sm: "1.1rem", // Tablet
                md: "1.15rem", // 13" лаптоп
                lg: "1.25rem", // Desktop
              },
              textAlign: "center",
            }}
          >
            Персонал и заплащане
          </Typography>

          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Показател
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Стойност
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Сравнение
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <MetricRow
                  label="Общ персонал на читалищата (НСИ, 2022)"
                  value={metrics.totalStaff}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Брой уникални лица на трудов договор (НАП, 2023)"
                  value={metrics.uniqueEmploymentContracts}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Процент от персонала с висше образование (НСИ, 2022)"
                  value={metrics.staffHigherEducationPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={
                    NATIONAL_AVERAGES.staffHigherEducationPercent
                  }
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Процент от персонала със средно образование (НСИ, 2022)"
                  value={metrics.staffSecondaryEducationPercent?.toFixed(1)}
                  suffix="%"
                  showComparison={false}
                />
                <MetricRow
                  label="Брой читалищни секретари (НСИ, 2022)"
                  value={metrics.secretariesCount}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Процент от читалищните секретари с висше образование"
                  value={metrics.secretariesHigherEducationPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={
                    NATIONAL_AVERAGES.secretariesHigherEducationPercent
                  }
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Среден осигурителен доход по трудов договор (НАП, 2023)"
                  value={metrics.averageInsuranceIncome?.toFixed(0)}
                  suffix="лв."
                  nationalAverage={NATIONAL_AVERAGES.averageInsuranceIncome}
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Процент читалища, които не са участвали в нито едно обучение (2023)"
                  value={metrics.chitalishtaNoTrainingPercent?.toFixed(1)}
                  suffix="%"
                  nationalAverage={
                    NATIONAL_AVERAGES.chitalishtaNoTrainingPercent
                  }
                  isHigherBetter={false}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Таб 4: Читалищата и населението */}
        <TabPanel value={tabValue} index={3}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.custom.primaryOrange,
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: {
                xs: "1rem", // Mobile
                sm: "1.1rem", // Tablet
                md: "1.15rem", // 13" лаптоп
                lg: "1.25rem", // Desktop
              },
              textAlign: "center",
            }}
          >
            Читалищата и населението
          </Typography>

          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Показател
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Стойност
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      fontSize: {
                        xs: "0.8rem", // Mobile
                        sm: "0.85rem", // Tablet
                        md: "0.88rem", // 13" лаптоп
                        lg: "0.95rem", // Desktop
                      },
                    }}
                  >
                    Сравнение
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <MetricRow
                  label="Читалища на 10 000 жители"
                  value={metrics.chitalishtaPer10kResidents?.toFixed(1)}
                  suffix="бр."
                  nationalAverage={NATIONAL_AVERAGES.chitalishtaPer10kResidents}
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Читалища на 1000 деца под 15 г."
                  value={metrics.chitalishtaPer1kChildrenUnder15?.toFixed(1)}
                  suffix="бр."
                  nationalAverage={
                    NATIONAL_AVERAGES.chitalishtaPer1kChildrenUnder15
                  }
                  isHigherBetter={true}
                />
                <MetricRow
                  label="Читалища на 1000 ученици"
                  value={metrics.chitalishtaPer1kStudents?.toFixed(1)}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Читалища на 1000 деца в детски градини"
                  value={metrics.chitalishtaPer1kKindergarten?.toFixed(1)}
                  suffix="бр."
                  showComparison={false}
                />
                <MetricRow
                  label="Читалища на 1000 жители на и над 65 г."
                  value={metrics.chitalishtaPer1kElderly?.toFixed(1)}
                  suffix="бр."
                  nationalAverage={NATIONAL_AVERAGES.chitalishtaPer1kElderly}
                  isHigherBetter={true}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Таб 5: Читалища в общината */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                mb: { xs: 2, sm: 2.5, md: 3 },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 1.5, md: 0 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.custom.primaryOrange,
                  fontSize: {
                    xs: "1rem", // Mobile
                    sm: "1.1rem", // Tablet
                    md: "1.15rem", // 13" лаптоп
                    lg: "1.25rem", // Desktop
                  },
                  textAlign: { xs: "center", sm: "left" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Читалища в общината
              </Typography>
              <Chip
                label={`${filteredChitalishta.length} от ${chitalishta.length}`}
                size="small"
                sx={{
                  bgcolor: theme.palette.custom.primaryOrange,
                  color: theme.palette.primary.contrastText,
                  fontSize: {
                    xs: "0.75rem", // Mobile
                    sm: "0.8rem", // Tablet
                    md: "0.85rem", // 13" лаптоп
                    lg: "0.9rem", // Desktop
                  },
                }}
              />
            </Box>

            {/* Търсачка */}
            <TextField
              fullWidth
              size="small"
              placeholder="Търсене по име или населено място..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize={isMobile ? "small" : "medium"} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: { xs: 2, sm: 2.5, md: 3 },
                maxWidth: { xs: "100%", sm: 400, md: 450, lg: 500 },
                "& .MuiInputBase-root": {
                  height: {
                    xs: 44, // Mobile
                    sm: 48, // Tablet
                    md: 52, // 13" лаптоп
                    lg: 56, // Desktop
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: {
                    xs: "0.9rem", // Mobile
                    sm: "0.95rem", // Tablet
                    md: "1rem", // 13" лаптоп
                    lg: "1.05rem", // Desktop
                  },
                },
              }}
            />
          </Box>

          {/* Grid с читалищата */}
          {chitalishtaLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={isMobile ? 36 : isTablet ? 40 : 44} />
            </Box>
          ) : filteredChitalishta.length === 0 ? (
            <Paper
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                textAlign: "center",
                bgcolor: "grey.50",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {searchQuery
                  ? "Няма резултати от търсенето"
                  : "Няма данни за читалища"}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5, lg: 3 }}>
              {filteredChitalishta.map((chitalishte) => {
                const borderColor = stringToColor(chitalishte.name);

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    lg={4}
                    xl={4}
                    key={chitalishte.id}
                  >
                    <Card
                      onClick={() => handleCardClick(chitalishte.slug)}
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderLeft: `4px solid ${borderColor}`,
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent
                        sx={{ p: { xs: 1.75, sm: 2, md: 2.25, lg: 2.5 } }}
                      >
                        {/* Заглавие */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: { xs: 0.75, sm: 1 },
                            lineHeight: 1.3,
                            minHeight: {
                              xs: 48, // Mobile
                              sm: 52, // Tablet
                              md: 56, // 13" лаптоп
                              lg: 60, // Desktop
                            },
                            fontSize: {
                              xs: "0.9rem", // Mobile
                              sm: "0.95rem", // Tablet
                              md: "1rem", // 13" лаптоп
                              lg: "1.05rem", // Desktop
                            },
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {chitalishte.name}
                        </Typography>

                        {/* Населено място */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: { xs: 1.25, sm: 1.5 },
                          }}
                        >
                          <LocationOnIcon
                            sx={{
                              fontSize: {
                                xs: 16, // Mobile
                                sm: 17, // Tablet
                                md: 18, // 13" лаптоп
                                lg: 19, // Desktop
                              },
                              color: "text.secondary",
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: {
                                xs: "0.8rem", // Mobile
                                sm: "0.85rem", // Tablet
                                md: "0.9rem", // 13" лаптоп
                                lg: "0.95rem", // Desktop
                              },
                            }}
                          >
                            {chitalishte.town}
                          </Typography>
                        </Box>

                        <Stack spacing={0.75}>
                          {/* Адрес */}
                          {chitalishte.address && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: {
                                  xs: "0.8rem", // Mobile
                                  sm: "0.85rem", // Tablet
                                  md: "0.9rem", // 13" лаптоп
                                  lg: "0.95rem", // Desktop
                                },
                              }}
                            >
                              📍 {chitalishte.address}
                            </Typography>
                          )}

                          {/* Имейл */}
                          {chitalishte.email && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <EmailIcon
                                sx={{
                                  fontSize: {
                                    xs: 14, // Mobile
                                    sm: 15, // Tablet
                                    md: 16, // 13" лаптоп и нагоре
                                  },
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="caption"
                                component="a"
                                href={`mailto:${chitalishte.email}`}
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "none",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: {
                                    xs: "0.8rem", // Mobile
                                    sm: "0.85rem", // Tablet
                                    md: "0.9rem", // 13" лаптоп
                                    lg: "0.95rem", // Desktop
                                  },
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {truncateText(
                                  chitalishte.email,
                                  isMobile ? 25 : isTablet ? 30 : 35
                                )}
                              </Typography>
                            </Box>
                          )}

                          {/* Уебсайт */}
                          {chitalishte.website && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <LanguageIcon
                                sx={{
                                  fontSize: {
                                    xs: 14, // Mobile
                                    sm: 15, // Tablet
                                    md: 16, // 13" лаптоп и нагоре
                                  },
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="caption"
                                component="a"
                                href={chitalishte.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "none",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: {
                                    xs: "0.8rem", // Mobile
                                    sm: "0.85rem", // Tablet
                                    md: "0.9rem", // 13" лаптоп
                                    lg: "0.95rem", // Desktop
                                  },
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                Уебсайт
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </TabPanel>
      </Box>
    </Paper>
  );
};

export default MunicipalityMetricsTabs;
